import { computed, watch, onMounted, type ComputedRef, type Ref } from "vue";
import { useHead } from "#imports";
import {
  GALLERY_WIDTHS,
  GALLERY_SIZES,
  GALLERY_QUALITY,
  THUMB_WIDTHS,
} from "~/utils/imageSizes";
import { useImageOptimizer } from "~/composables/useImageOptimizer";
import { selectGalleryImages } from "./galleryImages";

type AnyRecord = Record<string, unknown>;

/* ====================================================================
 *  useProductGallery
 *  – кадри галереї (варіант → товар) як проксі-URL;
 *  – <link rel="preload"> на LCP-кадр.
 *
 *  ЖОДНОГО Firebase Storage SDK (п.9г). Раніше кожен кадр резолвився
 *  через `getDownloadURL` під `import.meta.client`: сирий HTML не мав
 *  жодного `<img>`, а картинка починала вантажитись аж після гідрації,
 *  окремим запитом на кадр. Тепер це синхронний computed поверх чистого
 *  `selectGalleryImages`, тож галерея є вже в SSR — а заразом реальне
 *  фото отримують og:image і JSON-LD.
 *
 *  PRELOAD МУСИТЬ ЗБІГАТИСЬ ІЗ `<img>` (п.9г). Раніше він вказував на
 *  сирий оригінал і ніс `imagesrcset` з одного URL без `imagesizes` —
 *  тобто прогрівав не той файл, який потім вантажив браузер. Тепер
 *  href/imagesrcset/imagesizes рахуються тими самими ширинами і тим
 *  самим `sizes`, що й у ProductGallery: розбіжність тут означала б
 *  завантаження кадру двічі.
 *
 *  Preconnect на firebasestorage.googleapis.com звідси прибраний: цей
 *  хост сторінка більше не чіпає, картинки йдуть із власного домену.
 *  Preconnect на firestore лишається — сабколекцію варіантів клієнт
 *  усе ще дочитує (кадри 2..N).
 * ==================================================================== */
export function useProductGallery(
  product: Ref<AnyRecord | null | undefined>,
  currentVariant: Ref<AnyRecord | null | undefined>,
): { gallery: ComputedRef<string[]> } {
  const gallery = computed(() =>
    selectGalleryImages(product.value, currentVariant.value),
  );

  const { toSrc, toSrcset } = useImageOptimizer();
  const largestWidth = GALLERY_WIDTHS[GALLERY_WIDTHS.length - 1];

  const lcpFrame = computed(() => gallery.value[0] || "");
  // Якість МУСИТЬ збігатися з ProductGallery: інакше preload прогріє
  // інший файл, і кадр поїде двічі.
  const lcpSrc = computed(() =>
    toSrc(lcpFrame.value, largestWidth, GALLERY_QUALITY),
  );
  const lcpSrcset = computed(() =>
    toSrcset(lcpFrame.value, GALLERY_WIDTHS, GALLERY_QUALITY),
  );

  useHead(() => ({
    link: [
      {
        rel: "preconnect" as const,
        href: "https://firestore.googleapis.com",
        crossorigin: "" as const,
      },
      ...(lcpSrc.value
        ? [
            {
              rel: "preload" as const,
              as: "image" as const,
              href: lcpSrc.value,
              fetchpriority: "high" as const,
              ...(lcpSrcset.value
                ? {
                    imagesrcset: lcpSrcset.value,
                    imagesizes: GALLERY_SIZES,
                  }
                : {}),
            },
          ]
        : []),
    ],
  }));

  warmupNeighbours({ product, gallery, toSrc, toSrcset, largestWidth });

  return { gallery };
}

/* ====================================================================
 *  ПРОГРІВ СУСІДНІХ КАДРІВ (п.9г).
 *
 *  Проблема, яку лікує: перемикаєш колір — і півсекунди-секунду висить
 *  СТАРА картинка, а прев'ю стоять сірими плейсхолдерами. Це не баг
 *  рендеру: кожен кадр нового лота — холодний промах оптимізатора
 *  (Vercel іде в наш проксі, той у Storage, далі транскод), і швидше
 *  він не стане. Єдине, що допомагає, — щоб на момент кліку кадр уже
 *  лежав у кеші браузера.
 *
 *  Що саме гріємо (і чому не все підряд):
 *   – прев'ю всіх кадрів поточного лота: по ~3 КБ, це прямо ті сірі
 *     квадрати;
 *   – повнорозмірні перші кадри інших КОЛЬОРІВ: саме їх користувач
 *     побачить після кліку по свотчу.
 *  Обидва списки з межею і тільки на idle, щоб не конкурувати з LCP.
 *  Прогрів робиться картинкою поза DOM зі `srcset`+`sizes`, тобто
 *  браузер сам обере того ж кандидата, що потім піде в `<img>` —
 *  фіксована ширина промазала б повз кеш на іншому DPR.
 * ==================================================================== */

/**
 * Бюджети прогріву. Свідомо скромні: кожен повнорозмірний кадр — це
 * ~7–20 КБ avif на проді, і десяток таких на мобільному трафіку вже
 * не «безкоштовна оптимізація». Пріоритет — кольори (саме на них
 * скарга), потім власні кадри лота.
 */
const MAX_WARM_COLORS = 4;
const MAX_WARM_OWN_FRAMES = 3;

/** Чи доречно взагалі гріти: economy-режим і повільна мережа — ні. */
function warmupAllowed(): boolean {
  const conn = (
    navigator as unknown as {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return !/2g/.test(String(conn.effectiveType || ""));
}

/* eslint-disable no-unused-vars -- імена параметрів у сигнатурах типів */
interface WindowWithIdle {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
}

interface WarmupDeps {
  product: Ref<AnyRecord | null | undefined>;
  gallery: ComputedRef<string[]>;
  toSrc: (url: string, width: number, quality?: number) => string;
  toSrcset: (
    url: string,
    widths: number[],
    quality?: number,
  ) => string | undefined;
  largestWidth: number;
}
/* eslint-enable no-unused-vars */

function whenIdle(fn: () => void) {
  const ric = (window as unknown as WindowWithIdle).requestIdleCallback;
  if (ric) ric(fn, { timeout: 3000 });
  else setTimeout(fn, 1200);
}

function warmupNeighbours({
  product,
  gallery,
  toSrc,
  toSrcset,
  largestWidth,
}: WarmupDeps) {
  if (!import.meta.client) return;

  const warmed = new Set<string>();

  function warm(url: string, full: boolean) {
    if (!url) return;
    const key = `${full ? "full" : "thumb"}:${url}`;
    if (warmed.has(key)) return;
    warmed.add(key);

    const el = new Image();
    el.decoding = "async";
    el.fetchPriority = "low";
    if (full) {
      const srcset = toSrcset(url, GALLERY_WIDTHS, GALLERY_QUALITY);
      if (srcset) {
        el.sizes = GALLERY_SIZES;
        el.srcset = srcset;
      }
      el.src = toSrc(url, largestWidth, GALLERY_QUALITY);
    } else {
      el.src = toSrc(url, THUMB_WIDTHS[0]);
    }
  }

  // Кадри поточного лота: прев'ю — всі, повний розмір — лише ті, що
  // реально можуть відкритись кліком, і в межах ліміту.
  watch(
    gallery,
    (frames) => {
      whenIdle(() => {
        // Прев'ю гріємо завжди: це ті самі сірі квадрати, і вони по ~3 КБ.
        frames.forEach((url) => warm(url, false));
        if (!warmupAllowed()) return;
        frames
          .slice(1, 1 + MAX_WARM_OWN_FRAMES)
          .forEach((url) => warm(url, true));
      });
    },
    { immediate: true },
  );

  // Перші кадри інших кольорів — по одному на колір.
  onMounted(() => {
    whenIdle(() => {
      if (!warmupAllowed()) return;
      const list = Array.isArray(product.value?.variantsLite)
        ? (product.value?.variantsLite as AnyRecord[])
        : [];
      const seenColor = new Set<string>();
      let budget = MAX_WARM_COLORS;
      for (const v of list) {
        if (budget <= 0) break;
        const color = String((v?.color as AnyRecord)?.value ?? v?.color ?? "");
        if (color && seenColor.has(color)) continue;
        if (color) seenColor.add(color);
        const first = selectGalleryImages(product.value, v)[0];
        if (!first) continue;
        warm(first, true);
        budget -= 1;
      }
    });
  });
}
